/**
 * LineItemCommentThread
 * 
 * Displays comment thread for a specific line item within a finance document.
 * Extends TotalFinanceCommentsSection functionality for line-item context.
 */

'use client';

import React from 'react';
import { MessageSquare, X } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import TotalFinanceCommentsSection from '@/components/tabs/TotalFinanceCommentsSection';

export interface LineItemCommentThreadProps {
  documentId: string;
  lineItemId: string;
  lineItemDescription: string;
  isOpen: boolean;
  onClose: () => void;
  onCommentAdded?: () => void;
}

export function LineItemCommentThread({
  documentId,
  lineItemId,
  lineItemDescription,
  isOpen,
  onClose,
  onCommentAdded,
}: LineItemCommentThreadProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.mode === 'dark';

  if (!isOpen) {
    return null;
  }

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50`}>
      <div
        className={`relative w-full max-w-lg max-h-[80vh] rounded-xl shadow-2xl overflow-hidden ${
          isDark ? 'bg-gray-900' : 'bg-white'
        }`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Line Item Comments</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[300px]">
                {lineItemDescription}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors`}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Comments */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          <TotalFinanceCommentsSection
            workItemId={documentId}
            isActive={true}
            lineItemId={lineItemId}
            context="line_item"
            onCommentAdded={onCommentAdded}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Small button to open line-item comment thread
 */
export interface LineItemCommentButtonProps {
  commentCount: number;
  onClick: () => void;
}

export function LineItemCommentButton({
  commentCount,
  onClick,
}: LineItemCommentButtonProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.mode === 'dark';

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
        commentCount > 0
          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
          : isDark
            ? 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      <MessageSquare className="h-3.5 w-3.5" />
      <span>{commentCount}</span>
    </button>
  );
}
